import { z } from "zod";
import { activityIdSchema } from "@/validations/profile";

export const buddyRequestSchema = z
  .object({
    targetUserId: z.string().min(1, "Target user is required"),
    /**
     * `direct` is the one-tap add: no message, no activity, nothing for the
     * sender to fill in. The inbox derives its own context chips (mutuals,
     * shared hiives, shared activities) server-side rather than putting words
     * in the sender's mouth. `message` and `activity` are the opt-in richer
     * paths, still reachable from the buddy-request drawer.
     */
    intent: z.enum(["direct", "message", "activity"]),
    message: z
      .string()
      .trim()
      .max(500, "Messages can be up to 500 characters")
      .optional()
      .transform((value) => {
        if (!value) {
          return undefined;
        }

        return value.length > 0 ? value : undefined;
      }),
    activityId: activityIdSchema.optional(),
    activityNote: z
      .string()
      .trim()
      .max(280, "Keep the activity comment under 280 characters")
      .optional()
      .transform((value) => {
        if (!value) {
          return undefined;
        }

        return value.length > 0 ? value : undefined;
      }),
    /**
     * Which card sent this. Analytics only — never persisted, never shown to
     * the recipient. It is what tells us which surfaces actually produce
     * connections once quick-add is everywhere.
     */
    surface: z.string().trim().max(40).optional(),
  })
  .superRefine((value, ctx) => {
    if (value.intent === "direct") {
      if (value.message) {
        ctx.addIssue({
          code: "custom",
          path: ["message"],
          message: "A one-tap request carries no message",
        });
      }

      if (value.activityId || value.activityNote) {
        ctx.addIssue({
          code: "custom",
          path: ["activityId"],
          message: "A one-tap request carries no activity",
        });
      }
    }

    if (value.intent === "message") {
      if (!value.message) {
        ctx.addIssue({
          code: "custom",
          path: ["message"],
          message: "Add a short intro message",
        });
      }

      if (value.activityId || value.activityNote) {
        ctx.addIssue({
          code: "custom",
          path: ["activityId"],
          message: "Activity likes belong in the activity tab",
        });
      }
    }

    if (value.intent === "activity") {
      if (!value.activityId) {
        ctx.addIssue({
          code: "custom",
          path: ["activityId"],
          message: "Select an activity to like",
        });
      }
    }

    if (value.activityNote && !value.activityId) {
      ctx.addIssue({
        code: "custom",
        path: ["activityId"],
        message: "Select an activity to attach your comment",
      });
    }
  });

export type BuddyRequestInput = z.infer<typeof buddyRequestSchema>;

export const connectionRequestResponseSchema = z.object({
  requestId: z.number().int().positive("Request ID must be a positive number"),
});

export type ConnectionRequestResponseInput = z.infer<
  typeof connectionRequestResponseSchema
>;
